import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import Alert from "../Alert";
import LoadingSpinner from "../LoadingSpinner";
import ModalError from "../Modal/ModalError";
import { useSearchPlotsQuery } from "/src/redux/plotsApi";
import { camelCaseToCapitalCase } from "/src/utils/camelCase";

export default function MapSearch({onSelectPlot, onResults}){
    const [query, setQuery] = useState('');
    const [inputValue, setInputValue] = useState('');
    const { data: results, error: searchError, isLoading: isSearching } = useSearchPlotsQuery(query, {skip: !query.length});
    const inputEl = useRef(null);
    const [locateError, setLocateError] = useState(null);

    // debounce input
    useEffect(() => {
        const timeOutId = setTimeout(() => setQuery((inputValue.length < 2)?'':inputValue), 1000);
        return () => clearTimeout(timeOutId);
    }, [inputValue]);

    useEffect(()=>{
        if(inputEl.current) inputEl.current.focus();
    },[inputEl]);

    useEffect(()=>{
        if(results && !searchError && onResults){
            onResults(results.map(r=>r._id));
        }
    },[results, searchError, onResults]);

    // function findPathInObject(obj, value) {
    //     const searchRegexp = new RegExp(value, 'i');
    //     for(let key of Object.keys(obj)) {                          // for each key in obj (obj is either an object or an array) 
    //         if(obj.hasOwnProperty(key)){
    //             if(obj[key] && typeof obj[key] === "object") {      // if the current property (value of obj[key]) is also an object/array
    //                 let result = findPathInObject(obj[key], value); // try finding item in that object
    //                 if(result) {                                    // if we find it
    //                     result.unshift(key);                        // we shift the current key to the path array (result will be an array of keys)
    //                     return result;                              // and we return it to our caller
    //                 }
    //             } else if(searchRegexp.test(obj[key] + '')) {       // otherwise (if obj[key] is not an object or array) we check if it is the item we're looking for
    //                 console.debug(searchRegexp.test(obj[key] + ''))
    //                 return [key, value];                            // if it is then we return the path array (this is where the path array get constructed)
    //             }
    //         }
    //     }
    //     return [];
    // }

    function findResultPath(obj, value){
        const searchRegexp = new RegExp(value.split(' ').join('|'), 'i');
        const lotId = [obj.section, obj.lot, obj.lotPart].filter(p=>!!p).join('');
        if(searchRegexp.test(lotId)){
            return 'Lot ID';
        }else{
            // return findPathInObject(obj, value).map(key=>camelCaseToCapitalCase(key)).join(' - ');
            // Search top-level fields
            for (let key of ['notes', 'sizeNotes', 'date']){
                if(searchRegexp.test(obj[key])){
                    return `${camelCaseToCapitalCase(key)}: "${obj[key]}"`;
                }
            }
            // Search owners
            for (let person of obj.plotOwners || []){
                if(searchRegexp.test(person.firstName) || searchRegexp.test(person.lastName)){
                    return `Plot Owner: ${person.firstName} ${person.lastName}`;
                }
            }
            // Search occupants
            for (let person of obj.plotOccupants || []){
                if(searchRegexp.test(person.firstName) || searchRegexp.test(person.lastName)){
                    return `Plot Occupant: ${person.firstName} ${person.lastName}, ${person.deathDate}`;
                }
                if(searchRegexp.test(person.birthDate)){
                    return `Plot Occupant Birth Date: ${person.firstName} ${person.lastName}, ${person.birthDate}`;
                }
                if(searchRegexp.test(person.deathDate)){
                    return `Plot Occupant Death Date: ${person.firstName} ${person.lastName}, ${person.deathDate}`;
                }
            }
            return JSON.stringify(obj);
        }
    }

    function onResultClick(item){
        if(!item.lat || !item.lng){
            setLocateError( [item.section, item.lot, item.lotPart].filter(p=>!!p).join('') );
        }else{
            onSelectPlot(item._id);
        }
        
    }

    function closeOnEscape(e){
        if(e && e.key == 'Escape'){
            close();
        }
    }

    function close(){
        onSelectPlot(null);
    }

    return (
        <div className="absolute top-0 right-0 left-0 flex flex-col items-center max-w-[100vw] pointer-events-none" onKeyDown={closeOnEscape}>
            {locateError && <ModalError title="Cannot Locate Plot" message={`${locateError} exists in the database, but does not yet have a location on the map.`} description="Contact a staff member to have them add this entry to the map." callback={()=>setLocateError(false)} className="pointer-events-auto" />}
            <div className="w-1/2 m-4 relative z-0 pointer-events-auto">
                <form className="flex bg-white rounded-lg p-2 shadow-lg z-10 relative" onSubmit={e=>e.preventDefault()}>
                    <input type="text" max="256" className="flex-auto mr-1 px-2" value={inputValue} onChange={e=>setInputValue(e.target.value)} ref={inputEl} />
                    <button type="submit" tabIndex={-1}>
                        <MagnifyingGlassIcon className="w-8" />
                    </button>
                </form>
                <div className="bg-white border border-t-0 mx-4 p-4 z-0 relative max-h-96 overflow-hidden overflow-y-auto">
                    <ul>
                        {(searchError && (
                            <Alert className='border-t-red-600'>
                                <p>{searchError.data.message}</p>
                            </Alert>
                        )) || ((!inputValue || inputValue.length < 2) && (
                            <p>{inputValue.length?'Keep':'Start'} typing...</p>
                        )) || ((isSearching || query != inputValue) && (
                            <div className="flex">
                                <p>Searching...</p>
                                <LoadingSpinner className='w-6' />
                            </div>
                        )) ||  ((results && !results.length) && (
                            <p>No Results</p>
                        )) || (
                            <div>
                                {results && results.map(result=>(
                                    <li key={result._id}>
                                        <button type="button" className="w-full text-left py-2 px-4 border-b border-black hover:bg-gray-100" onClick={()=>onResultClick(result)}>
                                            <span className="font-bold">{result.section}{result.lot}{result.lotPart}</span>
                                            <hr className="border-transparent flex-auto"></hr>
                                            <span className="font-light">{findResultPath(result, query)}</span>
                                        </button>
                                    </li>
                                ))}
                                <p className="font-light sticky bottom-0 left-0 bg-white mt-2 border inline-block py-1 px-2 shadow-lg">{results.length} Result{(results.length > 1)?'s':''}</p>
                            </div>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}